import { useCallback } from 'react'
import { Avo } from '@thriveglobal/thrive-web-tracking'
import getTrackingProps from './getTrackingProps'

type ContentDownloadedTrackingProps = Parameters<
    typeof Avo.contentDownloaded
>[0]
type ContentDownloadedTrackingFields =
    | 'contentId'
    | 'contentTitle'
    | 'contentUrl'
    | 'contentType'
    | 'contentFormatType'
    | 'activityType'
    | 'webinarId'
    | 'webinarTitle'

type UseContentDownloadedTrackingProps = Partial<
    Pick<ContentDownloadedTrackingProps, ContentDownloadedTrackingFields>
>

const defaultContentDownloadedProps: Omit<
    ContentDownloadedTrackingProps,
    ContentDownloadedTrackingFields
> = {
    featureType: 'library',
    contentSubtype: null,
    contentSource: null,
    isSystemEvent: false
}

/**
 * Custom hook to track content downloaded events using Avo.
 *
 * This hook provides a function, `trackContentDownloaded`, that triggers the `Avo.contentDownloaded` event
 * with merged properties, including both default event props and user-provided props.
 *
 * @returns {(props: UseContentDownloadedTrackingProps) => void} - A callback function that can be called
 * to track the content downloaded event. It accepts `UseContentDownloadedTrackingProps`, which include
 * required event properties like `contentId`, `contentTitle`, and `contentUrl`.
 *
 * @example
 * const trackContentDownloaded = useContentDownloadedTracking();
 * trackContentDownloaded({
 *    contentId: '456',
 *    contentTitle: 'Audiobook Title',
 *    contentUrl: 'https://app.thriveglobal.com/book',
 *    contentType: 'audio_book',
 *    contentFormatType: 'audio',
 *    activityType: 'audiobook_downloaded
 * });
 */
const useContentDownloadedTracking = () => {
    /**
     * Tracks a content downloaded event using the `Avo.contentDownloaded` method.
     *
     * @param {UseContentDownloadedTrackingProps} props - The properties specific to the content downloaded event,
     * including `contentId`, `contentTitle`, and `contentUrl`.
     */
    return useCallback((props: UseContentDownloadedTrackingProps) => {
        Avo.contentDownloaded(
            getTrackingProps<ContentDownloadedTrackingProps>({
                ...defaultContentDownloadedProps,
                ...props
            })
        )
    }, [])
}

export default useContentDownloadedTracking
